import type { CleaningPreferences } from "@/hooks/useCleaningPreferences";

/**
 * Deterministic cleaning engine.
 *
 * Every number this returns is measured from the actual rows — nothing is
 * estimated or faked. The AI layer only *suggests* rules; the transformations
 * that change a user's data happen here so results are reproducible.
 */
export type Row = Record<string, any>;

export interface CleaningIssue {
  type: "duplicate" | "missing" | "format" | "outlier" | "invalid";
  column: string | null;
  description: string;
  count: number;
  severity: "low" | "medium" | "high";
}

export interface CleaningRunResult {
  rows: Row[];
  columns: string[];
  issues: CleaningIssue[];
  actions: { action: string; rows_affected: number }[];
  originalCount: number;
  cleanedCount: number;
}

export const getColumns = (rows: Row[]): string[] => {
  const set = new Set<string>();
  rows.forEach((r) => Object.keys(r ?? {}).forEach((k) => set.add(k)));
  return Array.from(set);
};

const isBlank = (v: any) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

const isNumericColumn = (rows: Row[], col: string) => {
  const values = rows.map((r) => r[col]).filter((v) => !isBlank(v));
  if (values.length === 0) return false;
  const numeric = values.filter(
    (v) => !isNaN(Number(String(v).replace(/[₦$£€,\s]/g, ""))),
  );
  return numeric.length / values.length > 0.8;
};

const toNumber = (v: any) => Number(String(v).replace(/[₦$£€,\s]/g, ""));

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d][\d\s().-]{6,}$/;

export function analyseRows(rows: Row[], prefs: CleaningPreferences): CleaningIssue[] {
  const issues: CleaningIssue[] = [];
  if (rows.length === 0) return issues;
  const cols = getColumns(rows);

  if (prefs.remove_duplicates) {
    const seen = new Set<string>();
    let dupes = 0;
    rows.forEach((r) => {
      const key = JSON.stringify(r);
      if (seen.has(key)) dupes++;
      else seen.add(key);
    });
    if (dupes > 0) {
      issues.push({
        type: "duplicate",
        column: null,
        description: `${dupes} duplicate row${dupes === 1 ? "" : "s"}`,
        count: dupes,
        severity: dupes > rows.length * 0.1 ? "high" : "medium",
      });
    }
  }

  cols.forEach((col) => {
    const missing = rows.filter((r) => isBlank(r[col])).length;
    if (missing > 0 && prefs.handle_missing_values) {
      issues.push({
        type: "missing",
        column: col,
        description: `${missing} empty value${missing === 1 ? "" : "s"} in "${col}"`,
        count: missing,
        severity:
          missing > rows.length * 0.3
            ? "high"
            : missing > rows.length * 0.1
            ? "medium"
            : "low",
      });
    }

    if (prefs.trim_whitespace) {
      const untrimmed = rows.filter(
        (r) => typeof r[col] === "string" && r[col] !== r[col].trim(),
      ).length;
      if (untrimmed > 0) {
        issues.push({
          type: "format",
          column: col,
          description: `${untrimmed} value${untrimmed === 1 ? "" : "s"} in "${col}" have extra spaces`,
          count: untrimmed,
          severity: "low",
        });
      }
    }

    if (prefs.standardize_formats && isNumericColumn(rows, col)) {
      const symbol = rows.filter(
        (r) => typeof r[col] === "string" && /[₦$£€,]/.test(r[col]),
      ).length;
      if (symbol > 0) {
        issues.push({
          type: "format",
          column: col,
          description: `${symbol} number${symbol === 1 ? "" : "s"} in "${col}" stored as text`,
          count: symbol,
          severity: "medium",
        });
      }
    }

    if (prefs.validate_emails && /mail/i.test(col)) {
      const bad = rows.filter(
        (r) => !isBlank(r[col]) && !EMAIL_RE.test(String(r[col]).trim()),
      ).length;
      if (bad > 0) {
        issues.push({
          type: "invalid",
          column: col,
          description: `${bad} invalid email address${bad === 1 ? "" : "es"} in "${col}"`,
          count: bad,
          severity: "medium",
        });
      }
    }

    if (prefs.validate_phones && /(phone|mobile|tel)/i.test(col)) {
      const bad = rows.filter(
        (r) => !isBlank(r[col]) && !PHONE_RE.test(String(r[col]).trim()),
      ).length;
      if (bad > 0) {
        issues.push({
          type: "invalid",
          column: col,
          description: `${bad} phone number${bad === 1 ? "" : "s"} in "${col}" look wrong`,
          count: bad,
          severity: "low",
        });
      }
    }

    if (prefs.remove_outliers && isNumericColumn(rows, col)) {
      const nums = rows.map((r) => toNumber(r[col])).filter((n) => !isNaN(n));
      const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
      const sd = Math.sqrt(
        nums.reduce((a, b) => a + (b - mean) ** 2, 0) / nums.length,
      );
      if (sd > 0) {
        const outliers = nums.filter((n) => Math.abs((n - mean) / sd) > 3).length;
        if (outliers > 0) {
          issues.push({
            type: "outlier",
            column: col,
            description: `${outliers} unusual value${outliers === 1 ? "" : "s"} in "${col}"`,
            count: outliers,
            severity: "medium",
          });
        }
      }
    }
  });

  return issues;
}

export function cleanRows(rows: Row[], prefs: CleaningPreferences): CleaningRunResult {
  const originalCount = rows.length;
  const issues = analyseRows(rows, prefs);
  const actions: { action: string; rows_affected: number }[] = [];
  let out: Row[] = rows.map((r) => ({ ...r }));
  const cols = getColumns(out);

  if (prefs.trim_whitespace) {
    let n = 0;
    out = out.map((r) => {
      const copy = { ...r };
      cols.forEach((c) => {
        if (typeof copy[c] === "string" && copy[c] !== copy[c].trim()) {
          copy[c] = copy[c].trim();
          n++;
        }
      });
      return copy;
    });
    if (n) actions.push({ action: "Removed extra spaces", rows_affected: n });
  }

  if (prefs.normalize_case) {
    let n = 0;
    out = out.map((r) => {
      const copy = { ...r };
      cols.forEach((c) => {
        if (typeof copy[c] === "string" && !isNumericColumn(rows, c)) {
          const next = copy[c].replace(/\b\w/g, (m: string) => m.toUpperCase());
          if (next !== copy[c]) n++;
          copy[c] = next;
        }
      });
      return copy;
    });
    if (n) actions.push({ action: "Made capitalisation consistent", rows_affected: n });
  }

  if (prefs.standardize_formats) {
    let numeric = 0;
    let dates = 0;
    cols.forEach((c) => {
      if (isNumericColumn(rows, c)) {
        out = out.map((r) => {
          if (!isBlank(r[c]) && typeof r[c] !== "number") {
            const n = toNumber(r[c]);
            if (!isNaN(n)) {
              r[c] = n;
              numeric++;
            }
          }
          return r;
        });
      } else if (/date|time|day/i.test(c)) {
        out = out.map((r) => {
          if (!isBlank(r[c])) {
            const d = new Date(r[c]);
            if (!isNaN(d.getTime())) {
              const iso = d.toISOString().split("T")[0];
              if (iso !== r[c]) dates++;
              r[c] = iso;
            }
          }
          return r;
        });
      }
    });
    if (numeric) actions.push({ action: "Converted text to numbers", rows_affected: numeric });
    if (dates) actions.push({ action: "Standardised dates to YYYY-MM-DD", rows_affected: dates });
  }

  if (prefs.handle_missing_values) {
    const strategy = prefs.missing_value_strategy;
    if (strategy === "drop") {
      const before = out.length;
      out = out.filter((r) => cols.every((c) => !isBlank(r[c])));
      if (before !== out.length) {
        actions.push({ action: "Removed rows with gaps", rows_affected: before - out.length });
      }
    } else {
      let filled = 0;
      cols.forEach((c) => {
        const present = out.map((r) => r[c]).filter((v) => !isBlank(v));
        if (present.length === 0) return;
        let replacement: any = "Unknown";
        if (strategy === "mean" && isNumericColumn(out, c)) {
          const nums = present.map(toNumber).filter((n) => !isNaN(n));
          replacement = nums.length
            ? Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2))
            : 0;
        } else if (strategy === "most_common") {
          const counts: Record<string, number> = {};
          present.forEach((v) => {
            counts[String(v)] = (counts[String(v)] || 0) + 1;
          });
          replacement = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
        } else if (strategy === "flag") {
          replacement = "Unknown";
        }
        out = out.map((r) => {
          if (isBlank(r[c])) {
            r[c] = replacement;
            filled++;
          }
          return r;
        });
      });
      if (filled) {
        actions.push({
          action:
            strategy === "flag"
              ? 'Marked empty cells as "Unknown"'
              : "Filled empty cells",
          rows_affected: filled,
        });
      }
    }
  }

  if (prefs.remove_duplicates) {
    const seen = new Set<string>();
    const before = out.length;
    out = out.filter((r) => {
      const key = JSON.stringify(r);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (before !== out.length) {
      actions.push({ action: "Removed duplicate rows", rows_affected: before - out.length });
    }
  }

  if (prefs.remove_outliers) {
    let removed = 0;
    cols.filter((c) => isNumericColumn(out, c)).forEach((c) => {
      const nums = out.map((r) => Number(r[c])).filter((n) => !isNaN(n));
      if (nums.length < 4) return;
      const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
      const sd = Math.sqrt(nums.reduce((a, b) => a + (b - mean) ** 2, 0) / nums.length);
      if (sd === 0) return;
      const before = out.length;
      out = out.filter((r) => {
        const n = Number(r[c]);
        return isNaN(n) || Math.abs((n - mean) / sd) <= 3;
      });
      removed += before - out.length;
    });
    if (removed) actions.push({ action: "Removed unusual values", rows_affected: removed });
  }

  return {
    rows: out,
    columns: getColumns(out),
    issues,
    actions,
    originalCount,
    cleanedCount: out.length,
  };
}

export function rowsToCSV(rows: Row[]): string {
  if (!rows.length) return "";
  const cols = getColumns(rows);
  const escape = (v: any) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))].join("\n");
}
